import React, { useCallback } from 'react';
import classNames from 'classnames';
import { IconButton } from '../buttons/IconButton';
import './RestorableListItem.less';

interface IRestorableListItemProps extends React.HTMLProps<HTMLLIElement> {
  onRestore: (isDeleted: boolean, e: React.MouseEvent<HTMLButtonElement>) => void;
  deleted?: boolean;
  /**
   * Determines restore button visibility
   * @default true
   */
  canRestore?: boolean;
  iconProps?: Omit<React.ComponentProps<'button'>, 'className' | 'onClick' | 'ref'>;
  children: React.ReactNode;
}

const getClassName = (className?: string) => classNames('dpc-restorable-list-item', className);

const getButtonIconClassName = (deleted?: boolean) =>
  classNames({
    'dpc-restorable-list-item__delete-icon': !deleted,
    'dpc-restorable-list-item__restore-icon': deleted,
  });

/**
 * List item with a delete/restore behavior. This component is not currently stateful.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export function RestorableListItem({
  className,
  children,
  deleted = false,
  canRestore = true,
  onRestore,
  iconProps,
  ...otherProps
}: IRestorableListItemProps) {
  const handleRestore = useCallback((e: React.MouseEvent<HTMLButtonElement>) => onRestore(!deleted, e), [
    deleted,
    onRestore,
  ]);

  return (
    <li className={getClassName(className)} {...otherProps}>
      {children}
      {canRestore && (
        <IconButton
          className="dpc-restorable-list-item__delete-button"
          iconClassName={getButtonIconClassName(deleted)}
          iconSize="small"
          aria-label="Restore"
          onClick={handleRestore}
          {...(iconProps || {})}
        />
      )}
    </li>
  );
}
